import React, { useEffect, useRef, useState } from "react";

function ThemeSettings() {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("app-theme") || "system"
  );
  const [bgImage, setBgImage] = useState(
    () => localStorage.getItem("app-bg-image") || ""
  );
  const fileRef = useRef(null);

  const PRESETS = [
    { id: "preset-1", label: "Default",   url: "../blackimg.png" },
    { id: "preset-2", label: "Mountains", url: "../bg1.avif" },
    { id: "preset-3", label: "Ocean",     url: "../bg2.jpg" },
    { id: "preset-4", label: "Forest",    url: "../bg3.webp" },
    { id: "preset-5", label: "Abstract",  url: "../bg4.avif" },
    { id: "preset-6", label: "Natural",   url: "../bg5.jpg" },
  ];

  const MODES = [
    {
      id: "light",
      label: "Light",
      icon: "☀️",
      desc: "Clean white interface",
    },
    {
      id: "dark",
      label: "Dark",
      icon: "🌙",
      desc: "Easy on the eyes",
    },
    {
      id: "system",
      label: "System",
      icon: "💻",
      desc: "Follows your device",
    },
  ];

  /* ── apply theme to <html> ── */
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "system") {
      // follow OS preference
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      root.setAttribute("data-theme", prefersDark ? "dark" : "light");
      localStorage.removeItem("app-theme");
    } else {
      root.setAttribute("data-theme", theme);
      localStorage.setItem("app-theme", theme);
    }
  }, [theme]);

  /* ── listen for OS theme changes when in system mode ── */
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      document.documentElement.setAttribute("data-theme", e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  /* ── apply background ── */
  useEffect(() => {
    if (bgImage) {
      document.body.style.backgroundImage    = `url(${bgImage})`;
      document.body.style.backgroundSize     = "cover";
      document.body.style.backgroundRepeat   = "no-repeat";
      document.body.style.backgroundPosition = "center center";
      localStorage.setItem("app-bg-image", bgImage);
      document.body.classList.add("has-custom-bg");
    } else {
      document.body.style.backgroundImage    = "";
      document.body.style.backgroundSize     = "";
      document.body.style.backgroundRepeat   = "";
      document.body.style.backgroundPosition = "";
      localStorage.removeItem("app-bg-image");
      document.body.classList.remove("has-custom-bg");
    }
  }, [bgImage]);

  /* ── file upload ── */
  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBgImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /* ── derive current active theme for display ── */
  const activeTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;

  return (
    <div className="theme-settings" style={{ display: "flex", flexDirection: "column", gap: 28 }}>

      {/* ══════════════════════════════════════════════════
          LIGHT / DARK / SYSTEM MODE
      ══════════════════════════════════════════════════ */}
      <section>
        <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "var(--color-text-primary, #111827)" }}>
          Appearance
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "var(--color-text-secondary, #6b7280)" }}>
          Choose how the interface looks
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {MODES.map((mode) => {
            const isActive = theme === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                onClick={() => setTheme(mode.id)}
                style={{
                  padding: "14px 10px",
                  border: isActive
                    ? "2px solid #2563eb"
                    : "1.5px solid var(--color-border, #e5e7eb)",
                  borderRadius: 12,
                  background: isActive
                    ? "rgba(37,99,235,0.07)"
                    : "var(--color-surface, #fff)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s",
                  position: "relative",
                  boxShadow: isActive ? "0 0 0 3px rgba(37,99,235,0.12)" : "none",
                }}
              >
                {/* Mode preview thumbnail */}
                <div style={{
                  width: "100%",
                  height: 52,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                  marginBottom: 4,
                  ...modePreviewStyle(mode.id),
                }}>
                  <ModePreview id={mode.id} />
                </div>

                <span style={{ fontSize: 18 }}>{mode.icon}</span>
                <span style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isActive ? "#2563eb" : "var(--color-text-primary, #111827)",
                }}>
                  {mode.label}
                </span>
                <span style={{
                  fontSize: 11,
                  color: "var(--color-text-secondary, #6b7280)",
                  textAlign: "center",
                  lineHeight: 1.3,
                }}>
                  {mode.desc}
                </span>

                {/* Active tick */}
                {isActive && (
                  <span style={{
                    position: "absolute", top: 6, right: 6,
                    background: "#2563eb", color: "#fff",
                    borderRadius: "50%", width: 18, height: 18,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700,
                  }}>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Live status pill */}
        <div style={{
          marginTop: 12,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: activeTheme === "dark" ? "#1e293b" : "#f1f5f9",
          color: activeTheme === "dark" ? "#94a3b8" : "#475569",
          borderRadius: 20,
          padding: "4px 12px",
          fontSize: 12,
          fontWeight: 500,
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: "50%",
            background: activeTheme === "dark" ? "#6366f1" : "#f59e0b",
            display: "inline-block",
          }} />
          Currently using <strong style={{ marginLeft: 3 }}>{activeTheme} mode</strong>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          BACKGROUND PRESETS
      ══════════════════════════════════════════════════ */}
      <section style={{ display:"none"}}>
        <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "var(--color-text-primary, #111827)" }}>
          Background presets
        </h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--color-text-secondary, #6b7280)" }}>
          Set a background image for the app
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setBgImage(p.url)}
              title={p.label}
              style={{
                padding: 0,
                border: bgImage === p.url ? "2px solid #2563eb" : "1px solid #ddd",
                borderRadius: 8,
                overflow: "hidden",
                background: "none",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <img
                src={p.url}
                alt={p.label}
                style={{ width: "100%", height: 72, objectFit: "cover", display: "block" }}
              />
              {bgImage === p.url && (
                <span style={{
                  position: "absolute", top: 4, right: 4,
                  background: "#2563eb", color: "#fff",
                  borderRadius: "50%", width: 18, height: 18,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700,
                }}>
                  ✓
                </span>
              )}
              <span style={{
                display: "block", fontSize: 11, textAlign: "center",
                padding: "3px 0", color: "#374151", fontWeight: 500,
              }}>
                {p.label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          CUSTOM BACKGROUND UPLOAD
      ══════════════════════════════════════════════════ */}
      <section style={{ display:"none"}}>
        <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "var(--color-text-primary, #111827)" }}>
          Custom background
        </h3>

        {bgImage ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 120, height: 72, borderRadius: 8, overflow: "hidden", border: "1px solid #ddd", flexShrink: 0 }}>
              <img src={bgImage} alt="Background preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" className="mail-button-secondary" onClick={() => fileRef.current?.click()}>
                Change image
              </button>
              <button type="button" className="mail-button-secondary" onClick={() => setBgImage("")} style={{ color: "#b91c1c" }}>
                Remove image
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="mail-profile-image-empty">No custom background selected.</p>
            <button type="button" className="mail-button-secondary" onClick={() => fileRef.current?.click()}>
              Upload image
            </button>
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
      </section>

    </div>
  );
}

/* ── Small SVG preview thumbnails ── */
function ModePreview({ id }) {
  if (id === "light") {
    return (
      <svg width="100%" height="52" viewBox="0 0 120 52" xmlns="http://www.w3.org/2000/svg">
        <rect width="120" height="52" fill="#f9fafb" />
        {/* sidebar */}
        <rect x="0" y="0" width="28" height="52" fill="#f3f4f6" />
        <rect x="4" y="6"  width="20" height="5" rx="2" fill="#d1d5db" />
        <rect x="4" y="14" width="16" height="4" rx="2" fill="#e5e7eb" />
        <rect x="4" y="21" width="18" height="4" rx="2" fill="#e5e7eb" />
        <rect x="4" y="28" width="14" height="4" rx="2" fill="#e5e7eb" />
        {/* chat area */}
        <rect x="34" y="8"  width="50" height="8" rx="4" fill="#e5e7eb" />
        <rect x="34" y="22" width="36" height="8" rx="4" fill="#dbeafe" />
        <rect x="60" y="35" width="36" height="8" rx="4" fill="#e5e7eb" />
      </svg>
    );
  }
  if (id === "dark") {
    return (
      <svg width="100%" height="52" viewBox="0 0 120 52" xmlns="http://www.w3.org/2000/svg">
        <rect width="120" height="52" fill="#0f172a" />
        <rect x="0" y="0" width="28" height="52" fill="#1e293b" />
        <rect x="4" y="6"  width="20" height="5" rx="2" fill="#334155" />
        <rect x="4" y="14" width="16" height="4" rx="2" fill="#334155" />
        <rect x="4" y="21" width="18" height="4" rx="2" fill="#334155" />
        <rect x="4" y="28" width="14" height="4" rx="2" fill="#334155" />
        <rect x="34" y="8"  width="50" height="8" rx="4" fill="#334155" />
        <rect x="34" y="22" width="36" height="8" rx="4" fill="#1d4ed8" />
        <rect x="60" y="35" width="36" height="8" rx="4" fill="#334155" />
      </svg>
    );
  }
  // system
  return (
    <svg width="100%" height="52" viewBox="0 0 120 52" xmlns="http://www.w3.org/2000/svg">
      {/* half light / half dark */}
      <rect width="60" height="52" fill="#f9fafb" />
      <rect x="60" width="60" height="52" fill="#0f172a" />
      {/* divider */}
      <line x1="60" y1="0" x2="60" y2="52" stroke="#6366f1" strokeWidth="2" />
      <rect x="4"  y="6"  width="16" height="4" rx="2" fill="#d1d5db" />
      <rect x="4"  y="14" width="12" height="3" rx="2" fill="#e5e7eb" />
      <rect x="4"  y="20" width="14" height="3" rx="2" fill="#e5e7eb" />
      <rect x="68" y="6"  width="16" height="4" rx="2" fill="#334155" />
      <rect x="68" y="14" width="12" height="3" rx="2" fill="#334155" />
      <rect x="68" y="20" width="14" height="3" rx="2" fill="#334155" />
      {/* sun / moon icons */}
      <circle cx="30" cy="38" r="5" fill="#f59e0b" />
      <circle cx="90" cy="38" r="5" fill="#6366f1" />
      <circle cx="93" cy="35" r="3" fill="#0f172a" />
    </svg>
  );
}

function modePreviewStyle(id) {
  return {};   // border/shadow handled by parent button
}

export default ThemeSettings;
